<template>
  <div class="wrapper">
    <div id="map"></div>
    <GeoNamesPopup
      v-if="showPopup"
      :concordance-word="concordanceWord"
      :execute-on-mount="executeOnMount"
      :publication-year="publicationYear"
      @hidePopup="hidePopup"
    />
  </div>
</template>

<script>
import * as timeline from "../leaflet.timeline.js";
import axios from "axios";
import GeoNamesPopup from "./GeoNamesPopup.vue";
let points = [];
let executeOnMount = false;
let concordanceWord = "";
let publicationYear = "";

export default {
  name: "GeoNames",
  components: { GeoNamesPopup },
  data() {
    return {
      executeOnMount: executeOnMount,
      concordanceWord: concordanceWord,
      publicationYear: publicationYear,
      geonames: [],
      search: "",
      loading: false,
      task: { id: null, status: null, finished: false },
      metadata_filters: [],
      show: { loading: false },
      showPopup: false,
    };
  },
  methods: {
    async startTask() {
      this.show.loading = true;
      let task = {
        task_type: "geo_proper_names",
        metadata_filter: this.metadata_filters,
        query_parameters: [],
        response_parameters: [],
      };

      try {
        let response = await axios.post(
          `${process.env.ROOT_API}startTask`,
          task,
          {
            headers: { "Content-Type": "application/json" },
            timeout: 5000,
          }
        );

        this.task.id = response.data.id;

        await this.checkStatus(this.task.id, 1000);
      } catch (e) {
        console.log(Object.keys(e), e.message);
      }
    },
    async checkStatus(taskId, timer) {
      try {
        let response = await axios.get(
          `${process.env.ROOT_API}getStatus/${taskId}`,
          {
            timeout: 10000,
          }
        );
        this.task.status = response.data.status;

        if (this.task.status === "DONE") {
          await this.getResult(taskId);
        } else if (this.task.status === "QUEUE") {
          await new Promise((resolve) => setTimeout(resolve, timer));
          await this.checkStatus(taskId, timer + 200);
        }
      } catch (e) {
        console.log(Object.keys(e), e.message);
      }
    },
    async getResult(taskId) {
      try {
        let response = await axios.get(
          `${process.env.ROOT_API}getResult/${taskId}`,
          {
            timeout: 5000,
          }
        );

        // merge all the data into one array
        const pointsArray = this.convertData(response.data.result.rows);

        points = pointsArray;
        this.task.finished = true;
        this.show.loading = false;
      } catch (e) {
        console.log(Object.keys(e), e.message);
      }
    },
    convertData(data) {
      // Flatten the array of arrays
      const flattenedData = data.flat();

      const convertedData = flattenedData.map((item) => {
        return [item[0], item[1], item[2], item[3], item[4]];
      });

      return convertedData;
    },
    hidePopup() {
      this.showPopup = false;
    },
    showConcordancePopup(pointData) {
      this.concordanceWord = pointData.lexeme;
      this.publicationYear = pointData.year;
      this.executeOnMount = true;
      this.showPopup = true;
    },
  },
  async mounted() {
    const plugin = document.createElement("script");
    plugin.setAttribute(
      "src",
      "https://cdn.jsdelivr.net/npm/leaflet.timeline@1.4.3/dist/leaflet.timeline.js"
    );
    plugin.async = true;
    document.head.appendChild(plugin);

    await this.startTask();

    const getQueryStringParams = (query) => {
      return query
        ? (/^[?#]/.test(query) ? query.slice(1) : query)
            .split("&")
            .reduce((params, param) => {
              const [key, value] = param.split("=");
              params[key] = value
                ? decodeURIComponent(value.replace(/\+/g, " "))
                : "";
              return params;
            }, {})
        : {};
    };

    const config = {
      bn: {
        menu: "National Library of Poland",
        types: {
          PUB: {
            menu: "publication places",
            radius: 1,
          },
          GEOS: {
            menu: "subject entries",
            radius: 5,
          },
          NER: {
            menu: "titles",
            radius: 5,
          },
        },
      },
      pbl: {
        menu: "Polish Literary Bibliography",
        types: {
          PUB: {
            menu: "publication places",
            radius: 1,
          },
        },
      },
    };

    function radius(r) {
      return Math.sqrt(r) * 300;
    }

    function convert(f) {
      let cor = 0;
      let type = "CI";
      if (f.length > 5) {
        cor = 0;
        type = f[3];
      }
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [f[4 + cor], f[3 + cor]],
        },
        properties: {
          start: f[0] + "-01-01",
          end: f[0] + "-12-31",
          type: type,
          popup: f[1],
          count: f[2],
        },
      };
    }

    var global = {
      pointTimeline: null,
      map: null,
      slider: null,
      current_time: -788918400000,
    };

    global.map = L.map("map", {
      center: [52.215933, 19.134422],
      crs: L.CRS.EPSG3857,
      zoom: 6,
      zoomControl: true,
      preferCanvas: false,
    });

    L.tileLayer(
      "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
      {
        attribution:
          '\u0026copy; \u003ca href="http://www.openstreetmap.org/copyright"\u003eOpenStreetMap\u003c/a\u003e contributors \u0026copy; \u003ca href="http://cartodb.com/attributions"\u003eCartoDB\u003c/a\u003e, CartoDB \u003ca href ="http://cartodb.com/attributions"\u003eattributions\u003c/a\u003e',
        detectRetina: false,
        maxNativeZoom: 18,
        maxZoom: 18,
        minZoom: 0,
        noWrap: false,
        opacity: 1,
        subdomains: "abc",
        tms: false,
      }
    ).addTo(global.map);

    global.slider = L.timelineSliderControl({
      formatOutput: function (date) {
        return moment(date).format("YYYY");
      },
      duration: 60000,
      enableKeyboardControls: true,
    });
    global.map.addControl(global.slider);
    global.map.createPane("paneLower").style.zIndex = 380;

    function update_slider(points3, bibliography, geos_type, context) {
      let points4 = {
        type: "FeatureCollection",
        features: points3.map(convert),
      };
      if (!!global.pointTimeline) {
        global.current_time = global.slider.time;
        global.slider.removeTimelines(global.pointTimeline);
        global.map.removeLayer(global.pointTimeline);
      }
      global.pointTimeline = L.timeline(points4, {
        pointToLayer: function (feature, latLng) {
          const _style = {
            fillColor: "#FF0000",
            fillOpacity: 0.5,
            weight: 0,
            stroke: false,
            radius: radius(feature.properties.count) * 3,
          };
          if (feature.properties.count > 800) {
            _style.pane = "paneLower";
          }
          const c = new L.Circle(latLng, _style);
          return c;
        },
        onEachFeature: function (feature, layer) {
          layer
            .bindTooltip(
              feature.properties.popup + " :" + feature.properties.count
            )
            .openTooltip();
          layer.on("click", function (e) {
            const pointData = {
              lexeme: feature.properties.popup,
              year: feature.properties.start.split("-")[0],
              type: feature.properties.type,
            };

            context.showConcordancePopup(pointData);
          });
        },
      });
      global.pointTimeline.addTo(global.map);
      global.slider.addTimelines(global.pointTimeline);
      if (!!global.current_time) global.slider.setTime(global.current_time);
    }

    const bibliography = "bn";
    const geosType = "PUB";

    // grab vue context
    const vm = this;

    update_slider(points, bibliography, geosType, vm);
  },
};
</script>

<style>
#map {
  height: 100%;
  margin: 0;
  padding: 0;
  width: 100%;
}
.leaflet-left {
  width: 100%;
}
.time-text {
  font-size: 26px;
  font-weight: bold;
}
@media screen and (min-width: 1076px) {
  .modal-dialog {
    max-width: 1000px;
  }
}
.table-cond {
  font-size: smaller;
  line-height: 1;
}
.wrapper,
#map,
.form {
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
}
.wrapper {
  position: relative;
}
#map,
.form {
  position: absolute;
  top: 0;
  left: 0;
}
.form {
  z-index: 100000;
  padding: 10px 60px;
  pointer-events: none;
}
.form > * {
  pointer-events: auto;
}
.bibliography {
  font-size: 12px;
  padding-bottom: 10px;
  letter-spacing: 1px;
}
.modal {
  padding: 50px;
}
</style>



// WEBPACK FOOTER //
// src/components/GeoNames.vue